
// services/tts/useDeepinfraTTS.js
import { useState, useRef, useCallback, useEffect } from "react";
import * as Sentry from "@sentry/react";
import audioManager from "../AudioManager";

function stripMarkdown(markdown) {
  return markdown
    // Remove fenced code blocks
    .replace(/```[\s\S]*?```/g, "")
    // Remove inline code
    .replace(/`([^`]+)`/g, "$1")
    // Remove images
    .replace(/!\[[^\]]*\]\([^\)]*\)/g, "")
    // Remove links but keep the text
    .replace(/\[([^\]]*)\]\([^\)]*\)/g, "$1")
    // Remove citation-like references [1], [1][2], etc.
    .replace(/\[(?:\d+\s*)+\]/g, "")
    // Remove numbered citations with URLs (e.g., "1) https://example.com")
    .replace(/^\d+\)\s*https?:\/\/[^\s]+$/gm, "")
    // Remove numbered citations at end of lines (e.g., "text [1]" or "text [1][2]")
    .replace(/\s*\[\d+(?:\]\[\d+)*\]\s*$/gm, "")
    // Remove standalone citation numbers with URLs on separate lines
    .replace(/^\s*\d+\)\s*https?:\/\/.*$/gm, "")
    // Remove citation blocks that start with numbers and contain URLs
    .replace(/^\s*\d+[\)\.]\s*.*?https?:\/\/[^\s\n]*.*$/gm, "")
    // Remove any remaining standalone URLs
    .replace(/^\s*https?:\/\/[^\s]+\s*$/gm, "")
    // Remove citation references in quotes (e.g., "1) "text"")
    .replace(/^\s*\d+\)\s*"[^"]*"\s*$/gm, "")
    // Remove markdown formatting symbols
    .replace(/([*_~#>`])/g, "")
    // Remove blockquotes
    .replace(/^>\s?/gm, "")
    // Remove headings
    .replace(/^#+\s?/gm, "")
    // Remove horizontal rules
    .replace(/^---$/gm, "")
    // Remove bullet points completely
    .replace(/^\s*[-*+]\s+/gm, "")
    // Remove emojis (covers wide Unicode emoji ranges)
    .replace(
      /[\u{1F300}-\u{1F6FF}\u{1F900}-\u{1FAFF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}]/gu,
      ""
    )
    // Clean up multiple newlines and normalize spaces
    .replace(/\n{3,}/g, "\n\n")
    .replace(/\s{2,}/g, " ")
    .trim();
}

/**
 * Convert PCM data to WAV format for proper audio playback
 * @param {ArrayBuffer} pcmData - Raw PCM data from backend
 * @param {number} sampleRate - Sample rate (default: 22050)
 * @param {number} numChannels - Number of channels (default: 1 for mono)
 * @returns {Blob} WAV audio blob
 */
function convertPCMToWAV(pcmData, sampleRate = 22050, numChannels = 1) {
  const pcmArray = new Int16Array(pcmData);
  const length = pcmArray.length;
  const buffer = new ArrayBuffer(44 + length * 2);
  const view = new DataView(buffer);

  // WAV header
  const writeString = (offset, string) => {
    for (let i = 0; i < string.length; i++) {
      view.setUint8(offset + i, string.charCodeAt(i));
    }
  };

  writeString(0, 'RIFF');
  view.setUint32(4, 36 + length * 2, true);
  writeString(8, 'WAVE');
  writeString(12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, numChannels, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * numChannels * 2, true);
  view.setUint16(32, numChannels * 2, true);
  view.setUint16(34, 16, true);
  writeString(36, 'data');
  view.setUint32(40, length * 2, true);

  // Copy PCM data
  const offset = 44;
  for (let i = 0; i < length; i++) {
    view.setInt16(offset + i * 2, pcmArray[i], true);
  }

  return new Blob([buffer], { type: 'audio/wav' });
}

/**
 * Generates TTS and converts PCM data to WAV for playback
 * Returns the WAV blob without automatic playback
 */
async function generateStreamingTTSInternal(text, voice, signal) {
  const url = new URL("llm/tts", import.meta.env.VITE_SOCKET_URL).href;
  const body = JSON.stringify({ text, voice });

  try {
    const res = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "audio/pcm", // Backend returns PCM data
      },
      body,
      signal,
    });

    if (!res.ok) {
      throw new Error(`Backend TTS error: ${res.status} ${res.statusText}`);
    }

    // Get the complete PCM data
    const pcmArrayBuffer = await res.arrayBuffer();

    if (!pcmArrayBuffer || pcmArrayBuffer.byteLength === 0) {
      throw new Error('Received empty audio data');
    }

    // Convert PCM to WAV for proper playback
    const wavBlob = convertPCMToWAV(pcmArrayBuffer, 22050, 1);

    return wavBlob;

  } catch (error) {
    Sentry.captureException(error, {
      extra: { 
        context: 'generateStreamingTTSInternal - TTS streaming generation',
        text: text?.substring(0, 100),
        voice,
        url,
        aborted: signal?.aborted
      }
    });

    throw error;
  }
}

/**
 * Calls your backend /llm/tts with { text, voice } and returns a Blob.
 * Properly handles PCM data from backend and converts to WAV.
 */
async function generateTTSInternal(text, voice, signal) {
  const url = new URL("llm/tts", import.meta.env.VITE_SOCKET_URL).href;
  const body = JSON.stringify({ text, voice });

  try {
    const res = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "audio/pcm", // Backend returns PCM data
      },
      body,
      signal,
    });

    if (!res.ok) {
      let msg = `Backend TTS error: ${res.status} ${res.statusText}`;
      try {
        const errText = await res.text();
        if (errText) msg += ` - ${errText}`;
      } catch {
        /* ignore */

      }
      throw new Error(msg);
    }

    // Get the complete PCM data
    const pcmArrayBuffer = await res.arrayBuffer();

    // Validate data
    if (!pcmArrayBuffer || pcmArrayBuffer.byteLength === 0) {
      throw new Error('Received empty audio data');
    }

    // Convert PCM to WAV for proper playback
    const wavBlob = convertPCMToWAV(pcmArrayBuffer, 22050, 1);
    return wavBlob;

  } catch (error) {
    Sentry.captureException(error, {
      extra: { 
        context: 'generateTTSInternal - TTS generation',
        text: text?.substring(0, 100),
        voice,
        url,
        aborted: signal?.aborted
      }
    });

    throw error;
  }
}

// Enhanced sentence splitting with markdown heading recognition and natural boundaries
function splitIntoSentences(text, minLength = 30, maxLength = 200) {
  // Step 1: Pre-process text and identify markdown headings
  const sentences = [];
  
  // Split text into lines to identify headings
  const lines = text.split('\n');
  let currentParagraph = '';
  
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();
    
    // Check if this line is a markdown heading
    const headingMatch = line.match(/^(#{1,6})\s+(.+)$/);
    
    if (headingMatch) {
      // Process any accumulated paragraph before the heading
      if (currentParagraph.trim()) {
        const paragraphSentences = processParagraph(currentParagraph.trim(), minLength, maxLength);
        sentences.push(...paragraphSentences);
        currentParagraph = '';
      }
      
      // Add the heading as a separate sentence (without the # symbols)
      const headingText = headingMatch[2].trim();
      if (headingText.length > 0) {
        sentences.push(headingText);

      }
    } else if (line.length > 0) {
      // Accumulate non-heading lines into current paragraph
      currentParagraph += (currentParagraph ? ' ' : '') + line;
    } else if (currentParagraph.trim()) {
      // Empty line - process accumulated paragraph
      const paragraphSentences = processParagraph(currentParagraph.trim(), minLength, maxLength);
      sentences.push(...paragraphSentences);
      currentParagraph = '';
    }
  }
  
  // Process any remaining paragraph
  if (currentParagraph.trim()) {
    const paragraphSentences = processParagraph(currentParagraph.trim(), minLength, maxLength);
    sentences.push(...paragraphSentences);
  }
  
  // Helper function to process regular paragraphs (non-headings)
  function processParagraph(text, minLen, maxLen) {
    // Fix common formatting issues
    text = text
      // Fix missing spaces after colons and other punctuation
      .replace(/([:.!?])([A-Z])/g, '$1 $2')
      // Fix missing spaces after list markers
      .replace(/(\d+\))([A-Z])/g, '$1 $2')
      // Fix missing spaces after bullet points
      .replace(/(•)([A-Z])/g, '$1 $2')
      // Remove inline citations like [1][2][3]
      .replace(/\[\d+(?:\]\[\d+)*\]/g, "")
      // Normalize multiple spaces
      .replace(/\s+/g, ' ')
      .trim();

    // Define common abbreviations that shouldn't trigger sentence splits
    const abbreviations = [
      'Dr', 'Mr', 'Mrs', 'Ms', 'Prof', 'Sr', 'Jr', 'Ph', 'M', 'B', 'A', 'D',
      'U.S', 'U.K', 'U.N', 'E.U', 'N.A.T.O', 'F.B.I', 'C.I.A', 'N.A.S.A',
      'vs', 'etc', 'i.e', 'e.g', 'cf', 'al', 'Inc', 'Corp', 'Ltd', 'Co',
      'St', 'Ave', 'Blvd', 'Rd', 'Ct', 'Pl', 'Sq', 'Ln',
      'Jan', 'Feb', 'Mar', 'Apr', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
      'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun',
      'a.m', 'p.m', 'AM', 'PM', 'No', 'Vol', 'pp', 'Fig', 'Ref', 'Eq'
    ];

    const abbrevPattern = abbreviations.map(abbr => abbr.replace(/\./g, '\\.')).join('|');
    
    // Split on sentence boundaries, avoiding abbreviations
    const parts = [];
    const tokens = text.match(/\S+|\s+/g) || [];
    let currentSentence = '';
    
    for (let i = 0; i < tokens.length; i++) {
      const token = tokens[i];
      currentSentence += token;
      
      // Check if this token ends with sentence-ending punctuation
      if (/[.!?]$/.test(token.trim())) {
        const nextToken = tokens[i + 1];
        const tokenAfterNext = tokens[i + 2];
        
        // Check if the next non-whitespace token starts with a capital letter
        const hasCapitalNext = nextToken && /^\s+$/.test(nextToken) && 
                              tokenAfterNext && /^[A-Z]/.test(tokenAfterNext);
        
        // Check if this token (without punctuation) is a known abbreviation
        const wordWithoutPunct = token.replace(/[.!?]+$/, '');
        const isAbbreviation = new RegExp(`^(${abbrevPattern})$`, 'i').test(wordWithoutPunct);
        
        // Split if: next token starts with capital AND current token is NOT an abbreviation AND sentence is reasonably long
        if (hasCapitalNext && !isAbbreviation && currentSentence.trim().length > 10) {
          parts.push(currentSentence.trim());
          currentSentence = '';
        }
      }
    }
    
    // Add any remaining content as the last sentence
    if (currentSentence.trim().length > 0) {
      parts.push(currentSentence.trim());
    }

    // Clean up sentences
    const cleanSentences = parts
      .map(s => stripMarkdown(s))
      .map(s => s.replace(/\s+([.,!?;:])/g, "$1")) // remove space before punctuation
      .map(s => s.replace(/\s+/g, " ")) // normalize spaces
      .map(s => s.trim())
      .filter(s => s.length > 0)
      // Filter out abbreviations that got split incorrectly
      .filter(s => {
        const isJustAbbrev = new RegExp(`^(${abbrevPattern})$`, 'i').test(s.replace(/[.!?]+$/, ''));
        return !isJustAbbrev;
      });

    return cleanSentences;
  }

  // Step 2: Smart chunking for optimal TTS processing
  const chunks = [];
  let currentChunk = "";

  for (let i = 0; i < sentences.length; i++) {
    const sentence = sentences[i];
    
    // Check if this is a heading (no markdown symbols, but likely a title)
    const isHeading = sentence.length < 100 && 
                     !sentence.match(/[.!?]$/) && 
                     sentence.match(/^[A-Z][^.!?]*$/);
    
    // Headings should be their own chunks
    if (isHeading) {
      // Finish current chunk if it exists
      if (currentChunk.trim()) {
        chunks.push(currentChunk.trim());
        currentChunk = "";
      }
      
      // Add heading as its own chunk
      chunks.push(sentence);

      continue;
    }
    
    const potentialChunk = currentChunk
      ? `${currentChunk} ${sentence}`
      : sentence;

    // More intelligent chunking decisions
    const shouldSplit = (
      potentialChunk.length > maxLength && 
      currentChunk.length > minLength
    ) || (
      // Split on list transitions
      currentChunk.includes(':') && 
      (sentence.startsWith('•') || /^\d+\)/.test(sentence)) &&
      currentChunk.length > minLength
    );

    if (shouldSplit) {
      chunks.push(currentChunk.trim());
      currentChunk = sentence;
    } else if (
      potentialChunk.length >= minLength ||
      i === sentences.length - 1
    ) {
      chunks.push(potentialChunk.trim());
      currentChunk = "";
    } else {
      currentChunk = potentialChunk;
    }
  }

  if (currentChunk.trim().length > 0) {
    chunks.push(currentChunk.trim());
  }

  return chunks.filter(chunk => chunk.length > 0);
}

export function useDeepinfraTTS({
  voice = "tara",
  enabled = true,
  minTextLength = 10,
  debug = false,
} = {}) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const abortControllerRef = useRef(null);

  // Proactive generation cache for first sentences
  const proactiveCache = useRef(new Map());

  const synthesize = useCallback(
    async ({ text, voiceOverride, signal }) => {
      if (!enabled || !text || text.trim().length === 0) {
        throw new Error("TTS is disabled or text is empty");
      }

      const plainText = stripMarkdown(text);
      if (plainText.length === 0) {
        throw new Error("Text is empty after markdown stripping");
      }

      const selectedVoice = voiceOverride || voice;

      setIsLoading(true);
      setError(null);
      try {
        const blob = await generateTTSInternal(plainText, selectedVoice, signal);
        return blob;
      } catch (err) {
        setError(err);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [enabled, voice, debug]
  );

  const synthesizeStreaming = useCallback(
    async ({ text, voiceOverride, signal, skipMarkdownStripping = false }) => {
      if (!enabled || !text || text.trim().length === 0) {
        throw new Error("TTS is disabled or text is empty");
      }

      // Only strip markdown if not already processed
      const plainText = skipMarkdownStripping ? text : stripMarkdown(text);
      if (plainText.length === 0) {
        throw new Error("Text is empty after markdown stripping");
      }

      const selectedVoice = voiceOverride || voice;

      setIsLoading(true);
      setError(null);
      try {
        const blob = await generateStreamingTTSInternal(plainText, selectedVoice, signal);
        return blob;
      } catch (err) {
        Sentry.captureException(err, {
          extra: { 
            context: 'synthesizeStreaming - TTS streaming synthesis',
            plainText: plainText?.substring(0, 100),
            selectedVoice,
            enabled,
            skipMarkdownStripping,
            aborted: signal?.aborted
          }
        });
        setError(err);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [enabled, voice, debug]
  );

  // HYBRID: Optimized parallel generation that works for both voice chat and read-aloud
  const processSequentialTTS = useCallback(
    async (text, onSentenceReady, signal) => {
      if (!enabled || !text || text.trim().length === 0) {
        return Promise.resolve();
      }

      // Pass original text to splitIntoSentences - it will handle markdown stripping internally
      const sentences = splitIntoSentences(text.trim(), minTextLength);

      if (sentences.length === 0) {

        return;
      }

      // CRITICAL FIX: Detect callback type to determine processing mode
      // Voice chat uses playAudioSequential (returns Promise)
      // Read-aloud uses regular playAudio (returns HTMLAudioElement)
      const isVoiceChatMode = onSentenceReady.toString().includes('playAudioSequential');

      if (isVoiceChatMode) {
        // VOICE CHAT MODE: Parallel generation with promise-based playback
        await processVoiceChatMode(sentences, onSentenceReady, signal);
      } else {
        // READ-ALOUD MODE: Parallel generation with traditional audio element playback
        await processReadAloudMode(sentences, onSentenceReady, signal);
      }

    },
    [enabled, minTextLength, debug, synthesizeStreaming, voice]
  );

  // Voice chat mode: Parallel generation with promise-based sequential playback
  const processVoiceChatMode = useCallback(
    async (sentences, onSentenceReady, signal) => {

      // State management for parallel generation + sequential playback
      let currentPlayingIndex = 0;
      let currentGeneratingIndex = 0;
      const audioQueue = new Map(); // Map<index, audioBlob>
      const generationPromises = new Map(); // Map<index, Promise>
      let isPlaying = false;

      // Function to generate audio for a specific sentence.
      // IMPORTANT: every exit path must populate audioQueue for this index
      // (even with `null` for "skip") - playNextInQueue/the polling loop
      // key off audioQueue.has(index), so a missing entry stalls playback
      // of this AND every subsequent sentence forever with no error shown.
      const markSkipped = (index) => {
        audioQueue.set(index, null);
        if (index === currentPlayingIndex && !isPlaying) {
          playNextInQueue();
        }
      };

      const generateSentence = async (index) => {
        if (signal?.aborted) {
          markSkipped(index);
          return null;
        }

        const sentence = sentences[index].trim();

        if (sentence.length < minTextLength) {
          markSkipped(index);
          return null;
        }

        try {

          // Check proactive cache for first sentence only
          let audioBlob = null;
          const cacheKey = `${sentence}_${voice}`;

          if (index === 0) {

            if (proactiveCache.current.has(cacheKey)) {
              const cached = proactiveCache.current.get(cacheKey);

              if (cached !== 'generating' && cached instanceof Blob) {

                audioBlob = cached;
                proactiveCache.current.delete(cacheKey);
              } else if (cached === 'generating') {

              }
            } else {

            }
          }

          // Generate if not cached
          if (!audioBlob) {
            audioBlob = await synthesizeStreaming({
              text: sentence,
              signal,
              skipMarkdownStripping: true,
            });
          }

          if (audioBlob && !signal?.aborted) {

            audioQueue.set(index, audioBlob);

            // Start playback if this is the next sentence to play and we're not already playing
            if (index === currentPlayingIndex && !isPlaying) {
              playNextInQueue();
            }

            return audioBlob;
          }

          // synthesizeStreaming resolved but produced nothing playable
          // (empty blob, or aborted mid-flight) - don't leave this index stuck.
          markSkipped(index);
          return null;
        } catch (error) {
          if (error.name !== "AbortError") {
            Sentry.captureException(error, {
              extra: {
                context: 'generateSentence - TTS sentence generation',
                sentence: sentence?.substring(0, 100),
                index,
                totalSentences: sentences.length,
                voice,
                aborted: signal?.aborted
              }
            });

          }
          markSkipped(index);
          return null;
        }
      };

      // Function to play the next available audio in sequence
      const playNextInQueue = async () => {
        if (isPlaying || signal?.aborted || currentPlayingIndex >= sentences.length) {
          return;
        }

        // Check if the next sentence to play is ready
        if (!audioQueue.has(currentPlayingIndex)) {

          return;
        }

        isPlaying = true;
        const audioBlob = audioQueue.get(currentPlayingIndex);
        const playingIndex = currentPlayingIndex;
        currentPlayingIndex++;

        try {

          // Start generating the next sentence while this one plays (if not already generating)
          if (currentGeneratingIndex < sentences.length) {
            const nextGenIndex = currentGeneratingIndex;
            currentGeneratingIndex++;
            
            if (!generationPromises.has(nextGenIndex)) {

              generationPromises.set(nextGenIndex, generateSentence(nextGenIndex));
            }
          }

          // audioBlob is null for sentences that were skipped (too short,
          // empty, or failed generation) - nothing to play, just move on.
          if (audioBlob) {
            await onSentenceReady(audioBlob);
          }

          // Clean up
          audioQueue.delete(playingIndex);

        } catch (error) {
          if (error.message?.includes('abort') || error.message?.includes('interrupt')) {

            return;
          } else {

          }
        } finally {
          isPlaying = false;
          
          // Continue with next sentence if available
          if (currentPlayingIndex < sentences.length && !signal?.aborted) {
            setTimeout(() => {
              playNextInQueue();
            }, 50);
          }
        }
      };

      // Start the process by generating the first sentence

      generationPromises.set(0, generateSentence(0));
      currentGeneratingIndex = 1;

      // Wait for all sentences to be processed and played
      while (currentPlayingIndex < sentences.length && !signal?.aborted) {
        await new Promise(resolve => setTimeout(resolve, 100));
        
        if (!isPlaying && audioQueue.has(currentPlayingIndex)) {
          playNextInQueue();
        }
      }

      // Wait for any remaining generation to complete
      try {
        await Promise.all(Array.from(generationPromises.values()));
      } catch (error) {
        if (error.name !== "AbortError") {

        }
      }
    },
    [minTextLength, synthesizeStreaming, voice]
  );

  // Read-aloud mode: Parallel generation with traditional audio element playback
  const processReadAloudMode = useCallback(
    async (sentences, onSentenceReady, signal) => {

      // CRITICAL FIX: Check for cached first sentence IMMEDIATELY
      const audioQueue = [];
      let firstSentenceCached = false;

      if (sentences.length > 0) {
        const firstSentence = sentences[0].trim();
        const cacheKey = `${firstSentence}_${voice}`;

        if (proactiveCache.current.has(cacheKey)) {
          const cached = proactiveCache.current.get(cacheKey);

          if (cached !== 'generating' && cached instanceof Blob) {

            audioQueue[0] = cached;
            firstSentenceCached = true;
            proactiveCache.current.delete(cacheKey);
          } else if (cached === 'generating') {

          }
        } else {

        }
      }

      let currentIndex = 0;
      let isGenerating = false;

      // Function to generate sentences (skip first if cached)
      const generateSentences = async (startIndex, endIndex) => {
        if (isGenerating) return;
        
        isGenerating = true;

        const generationPromises = [];
        
        for (let i = startIndex; i < endIndex; i++) {
          if (signal?.aborted) break;
          if (i === 0 && firstSentenceCached) {

            continue; // Skip first sentence if we have it cached
          }
          
          const sentence = sentences[i].trim();
          if (sentence.length < minTextLength) continue;
          
          const generatePromise = (async () => {
            try {

              const audioBlob = await synthesizeStreaming({
                text: sentence,
                signal,
                skipMarkdownStripping: true,
              });

              return { index: i, audioBlob };
            } catch (error) {
              if (error.name !== "AbortError") {

              }
              return { index: i, audioBlob: null };
            }
          })();
          
          generationPromises.push(generatePromise);
        }
        
        if (generationPromises.length > 0) {
          const results = await Promise.all(generationPromises);
          
          // Add results to queue in order
          for (const result of results) {
            if (result.audioBlob && !signal?.aborted) {
              audioQueue[result.index] = result.audioBlob;

            }
          }
        }
        
        isGenerating = false;
      };

      // CRITICAL FIX: If first sentence is cached, start playing immediately
      if (firstSentenceCached && audioQueue[0]) {

        // Start generating other sentences in background while first plays
        generateSentences(1, Math.min(3, sentences.length)); // Generate sentences 2-3
        
        try {
          // Play the cached first sentence immediately
          await onSentenceReady(audioQueue[0]);

        } catch (error) {
          if (error.name !== "AbortError") {

          }
        }
        
        // Continue with remaining sentences
        currentIndex = 1;
      } else {
        // No cached first sentence, generate first batch normally
        const firstBatchEnd = Math.min(2, sentences.length);
        await generateSentences(0, firstBatchEnd);
        currentIndex = 0;
      }

      // Process remaining sentences sequentially
      for (let i = currentIndex; i < sentences.length; i++) {
        if (signal?.aborted) break;

        const sentence = sentences[i].trim();
        if (sentence.length < minTextLength) continue;

        // Wait for this sentence's audio to be ready
        while (!audioQueue[i] && !signal?.aborted) {
          await new Promise(resolve => setTimeout(resolve, 50));
        }

        if (signal?.aborted) break;

        const audioBlob = audioQueue[i];
        if (!audioBlob) continue;

        try {

          // Start generating next sentences while this one plays
          const nextBatchStart = i + 2;
          const nextBatchEnd = Math.min(nextBatchStart + 2, sentences.length);
          if (nextBatchStart < sentences.length && !isGenerating) {
            generateSentences(nextBatchStart, nextBatchEnd);
          }

          // Call the read-aloud callback
          await onSentenceReady(audioBlob);

        } catch (error) {
          if (error.name !== "AbortError") {

          }
        }

        // Small delay between sentences for natural flow
        if (i < sentences.length - 1 && !signal?.aborted) {
          await new Promise(resolve => setTimeout(resolve, 100));
        }
      }
    },
    [minTextLength, synthesizeStreaming, voice]
  );

  // FIXED: Separate methods for different use cases to avoid breaking existing functionality
  
  // Original playAudio method for regular TTS (read-aloud button) - returns audio element
  const playAudio = useCallback((audioBlob, options = { autoPlay: true, volume: 1 }) => {
    if (!audioBlob || audioBlob.size === 0) {
      throw new Error('Invalid audio blob provided');
    }

    // CRITICAL FIX: Ensure audio blob is valid before playing
    if (audioBlob.type && !audioBlob.type.includes('audio')) {

    }
    
    // For regular TTS, use AudioManager to maintain singleton behavior (stops previous audio)
    return audioManager.playTTS(audioBlob, options);
  }, []);

  // New method specifically for sequential TTS (voice chat) - returns promise
  const playAudioSequential = useCallback((audioBlob, options = { autoPlay: true, volume: 1 }) => {
    if (!audioBlob || audioBlob.size === 0) {
      throw new Error('Invalid audio blob provided');
    }

    // CRITICAL FIX: Ensure audio blob is valid before playing
    if (audioBlob.type && !audioBlob.type.includes('audio')) {

    }
    
    // For sequential TTS, create independent audio elements to prevent overlap
    const audio = new Audio();
    audio.src = URL.createObjectURL(audioBlob);
    audio.volume = options.volume;
    audio.preload = 'auto';
    
    // Track this audio element
    activeAudioElements.current.add(audio);
    
    // Return a promise that resolves when audio finishes playing
    return new Promise((resolve, reject) => {
      const cleanup = () => {
        activeAudioElements.current.delete(audio);
        if (audio.src && audio.src.startsWith('blob:')) {
          URL.revokeObjectURL(audio.src);
        }
      };

      // Handle successful completion
      audio.addEventListener('ended', () => {

        cleanup();
        resolve(audio);
      });

      // Handle errors
      audio.addEventListener('error', (error) => {

        cleanup();
        reject(error);
      });

      // Auto-play if requested
      if (options.autoPlay) {
        const playAudioElement = async () => {
          try {

            await audio.play();

          } catch (error) {

            // Retry after short delay
            setTimeout(async () => {
              try {
                await audio.play();

              } catch (retryError) {

                cleanup();
                reject(retryError);
              }
            }, 100);
          }
        };
        
        playAudioElement();
      } else {
        // If not auto-playing, resolve immediately with the audio element
        resolve(audio);
      }
    });
  }, []);

  const pauseAudio = useCallback(() => {
    audioManager.pauseForSTT();
  }, []);

  const resumeAudio = useCallback(() => {
    audioManager.resumeAfterSTT();
  }, []);

  // Track active audio elements for proper cleanup
  const activeAudioElements = useRef(new Set());

  const stopAudio = useCallback(() => {

    // Stop all active TTS audio elements
    activeAudioElements.current.forEach(audio => {
      if (audio && !audio.paused) {
        audio.pause();
        audio.currentTime = 0;
      }
      if (audio.src && audio.src.startsWith('blob:')) {
        URL.revokeObjectURL(audio.src);
      }
    });
    
    // Clear the set
    activeAudioElements.current.clear();
    
    // Also stop any audio managed by AudioManager
    audioManager.stopAll();
  }, []);

  const getAudioState = useCallback(() => {
    return audioManager.getState();
  }, []);

  // FIXED: Use same sentence splitting logic as actual playback to ensure cache hits
  const generateProactiveFirstSentence = useCallback(
    async (text) => {
      if (!enabled || !text || text.trim().length === 0) return;

      // CRITICAL FIX: Use the same splitIntoSentences logic as actual playback
      const sentences = splitIntoSentences(text.trim(), minTextLength);

      if (sentences.length === 0) {

        return;
      }

      const firstSentence = sentences[0].trim();

      if (!firstSentence || firstSentence.length < minTextLength) {

        return;
      }

      const cacheKey = `${firstSentence}_${voice}`;

      // Don't generate if already cached or currently generating
      if (proactiveCache.current.has(cacheKey)) {

        return;
      }

      // Mark as generating
      proactiveCache.current.set(cacheKey, 'generating');

      try {
        // Pass skipMarkdownStripping: true since text is already processed by splitIntoSentences
        const audioBlob = await synthesizeStreaming({
          text: firstSentence,
          signal: new AbortController().signal,
          skipMarkdownStripping: true,
        });

        if (audioBlob) {
          proactiveCache.current.set(cacheKey, audioBlob);

        }

      } catch (error) {
        // Remove from cache on error
        proactiveCache.current.delete(cacheKey);

      }
    },
    [enabled, voice, minTextLength, synthesizeStreaming]
  );

  const cancelGeneration = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }

    // Clear proactive cache
    proactiveCache.current.clear();
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      
      // Stop all active audio elements
      activeAudioElements.current.forEach(audio => {
        if (audio && !audio.paused) {
          audio.pause();
          audio.currentTime = 0;
        }
        if (audio.src && audio.src.startsWith('blob:')) {
          URL.revokeObjectURL(audio.src);
        }
      });
      activeAudioElements.current.clear();
    };
  }, []);

  return {
    synthesize,
    synthesizeStreaming,
    processSequentialTTS,
    generateProactiveFirstSentence,

    playAudio,
    playAudioSequential, // New method for voice chat sequential playback
    pauseAudio,
    resumeAudio,
    stopAudio,
    getAudioState,

    cancelGeneration,

    isLoading,
    error,
    isEnabled: enabled,
    voice,

    stripMarkdown,
    splitIntoSentences,
  };
}